/* eslint-disable @typescript-eslint/naming-convention */
import { Patient, PatientFormsContext, PatientFormsEvent } from './patient-forms.types';

type EventOf<T extends PatientFormsEvent['type']> = Extract<PatientFormsEvent, { type: T }>;
type StateMeta = { state: { matches: (value: string) => boolean } };

const MIN_DOB = new Date('1900-01-01');
const MAX_DOB = new Date();

const isValidDateOfBirth = (dateOfBirth: string): boolean => {
	const date = new Date(dateOfBirth);

	return !isNaN(date.getTime()) && date >= MIN_DOB && date <= MAX_DOB;
};

/** Shared logic: card fields are editable when iTero is creating and Invisalign is none/creating */
export const checkCardEditable = (state: { matches: (value: string) => boolean }): boolean => {
	return (
		state.matches('patientCard.iTeroPatient.creating') &&
		(state.matches('patientCard.invisalignPatient.none') || state.matches('patientCard.invisalignPatient.creating'))
	);
};

export const guards = {
	isAlreadyLinkedPatient: (_context: PatientFormsContext, event: EventOf<'SEARCH.PATIENT_SELECTED'>): boolean => {
		return event.patient.system === 'both';
	},

	isNotAlreadyLinkedPatient: (_context: PatientFormsContext, event: EventOf<'SEARCH.PATIENT_SELECTED'>): boolean => {
		return event.patient.system !== 'both';
	},

	shouldRunPatientMatch: (_context: PatientFormsContext, event: EventOf<'SEARCH.PATIENT_SELECTED'>, meta: StateMeta): boolean => {
		if (event.patient.system === 'both') {
			return false;
		}

		if (meta.state.matches('patientCard.invisalignPatient.creating')) {
			const patient = event.patient;

			if (!patient.firstName || !patient.lastName || !patient.dateOfBirth || !patient.gender) {
				return false;
			}
		}

		return true;
	},

	needsInvisalignFieldsFilling: (_context: PatientFormsContext, event: EventOf<'SEARCH.PATIENT_SELECTED'>, meta: StateMeta): boolean => {
		if (!meta.state.matches('patientCard.invisalignPatient.creating')) {
			return false;
		}

		const patient: Patient = event.patient;

		return !patient.firstName || !patient.lastName || !patient.dateOfBirth || !patient.gender;
	},

	isPendingIteroOrLinked: (context: PatientFormsContext): boolean => {
		const patient = context.pendingIteroPatient;

		if (!patient) {
			return false;
		}

		return patient.system === 'iTero' || patient.system === 'both';
	},

	isPendingInvisalignOnly: (context: PatientFormsContext): boolean => {
		const patient = context.pendingInvisalignPatient;

		if (!patient) {
			return false;
		}

		return patient.system === 'Invisalign' && !context.pendingIteroPatient;
	},

	pendingNeedsInvisalignFields: (context: PatientFormsContext, _event: PatientFormsEvent, meta: StateMeta): boolean => {
		if (!meta.state.matches('patientCard.invisalignPatient.creating')) {
			return false;
		}

		const patient = context.pendingIteroPatient;

		if (!patient) {
			return false;
		}

		return !patient.firstName || !patient.lastName || !patient.dateOfBirth || !patient.gender;
	},

	isInvisalignToggleOn: (_context: PatientFormsContext, _event: PatientFormsEvent, meta: StateMeta): boolean => {
		return meta.state.matches('patientCard.invisalignPatient.creating');
	},

	isCardEditable: (_context: PatientFormsContext, _event: PatientFormsEvent, meta: StateMeta): boolean => {
		return checkCardEditable(meta.state);
	},

	hasPendingIteroPatient: (context: PatientFormsContext): boolean => {
		return context.pendingIteroPatient !== null;
	},

	canCommitFromCard: (context: PatientFormsContext, _event: PatientFormsEvent, meta: StateMeta): boolean => {
		const patient = context.iTeroPatient;

		if (!patient || !patient.firstName || !patient.lastName) {
			return false;
		}

		if (patient.dateOfBirth && !isValidDateOfBirth(patient.dateOfBirth)) {
			return false;
		}

		if (meta.state.matches('patientCard.invisalignPatient.creating')) {
			return !!patient.dateOfBirth && !!patient.gender;
		}

		return true;
	},

	isFillingInvisalignFields: (context: PatientFormsContext, _event: PatientFormsEvent, meta: StateMeta): boolean => {
		return (
			meta.state.matches('patientEditor.fillingInvisalignFields') &&
			meta.state.matches('patientCard.invisalignPatient.creating') &&
			meta.state.matches('patientCard.iTeroPatient.creating') &&
			context.pendingIteroPatient !== null
		);
	},

	isIteroSelected: (_context: PatientFormsContext, _event: PatientFormsEvent, meta: StateMeta): boolean => {
		return meta.state.matches('patientCard.iTeroPatient.selected');
	},

	isIteroSourceLink: (_context: PatientFormsContext, event: EventOf<'LINKING.LINKED'>): boolean => {
		return event.source === 'iTero';
	},

	isInvisalignSourceLink: (_context: PatientFormsContext, event: EventOf<'LINKING.LINKED'>): boolean => {
		return event.source === 'Invisalign';
	},

	wasInvisalignToggleOn: (context: PatientFormsContext): boolean => {
		return context.defaultInvisalignToggle;
	},

	isDefaultToggleOn: (_context: PatientFormsContext, event: EventOf<'CARD.SET_DEFAULT_TOGGLE'>): boolean => {
		return event.on;
	},

	isDefaultToggleOff: (_context: PatientFormsContext, event: EventOf<'CARD.SET_DEFAULT_TOGGLE'>): boolean => {
		return !event.on;
	},

	isLoadedPatientLinked: (_context: PatientFormsContext, event: EventOf<'EXISTING.PATIENT_LOADED'>): boolean => {
		return event.patient.system === 'both';
	},

	committedPatientNeedsInvisalignFields: (context: PatientFormsContext, _event: PatientFormsEvent, meta: StateMeta): boolean => {
		if (!meta.state.matches('patientCard.iTeroPatient.selected')) {
			return false;
		}

		if (meta.state.matches('patientCard.invisalignPatient.creating')) {
			return false;
		}

		const patient = context.iTeroPatient;

		if (!patient) {
			return false;
		}

		return !patient.dateOfBirth || !patient.gender;
	},

	isCheckingDuplicateFromLink: (_context: PatientFormsContext, _event: PatientFormsEvent, meta: StateMeta): boolean => {
		return meta.state.matches('patientEditor.checkingDuplicateFromLink');
	},

	shouldRunMatchAfterDuplicateCheck: (context: PatientFormsContext, _event: PatientFormsEvent, meta: StateMeta): boolean => {
		return context.pendingIteroPatient !== null && !meta.state.matches('patientEditor.checkingDuplicateFromLink');
	}
};
